import { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Check, X, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { useToast } from './Toast';
import { ConfirmModal } from './ConfirmModal';
import { formatDateTime } from '../../utils/format';

/**
 * The structural shape every comment row in this app already has. ArComment and
 * ConsBusinessComment both satisfy it as-is — no mapping layer, no domain types here.
 *
 * The Sync* fields are OPTIONAL and domain-neutral: a caller whose comments mirror into
 * an external system (consolidated-business → HubSpot notes) maps its own DTO onto them;
 * everyone else leaves them absent and nothing renders.
 */
export interface CommentItem {
    Id: number;
    CommentText: string;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedAt: string | null;
    /** 'Synced' | 'NoDeal' | 'Disabled' | 'Failed' — absent means "no external sync". */
    SyncStatus?: string | null;
    /** Deep link to the mirrored record; rendered only while SyncStatus is 'Synced'. */
    SyncUrl?: string | null;
    /** Tooltip detail for non-Synced states (e.g. the error text). */
    SyncHint?: string | null;
}

/**
 * The four operations the thread needs. Injected by the caller so this component stays
 * domain-free: the caller decides which table, which entity arm and which API client.
 *
 * IMPORTANT: pass a MEMOISED adapter (useMemo on the identifying key). The load effect
 * depends on it, so a fresh object every render would re-fetch in a loop.
 *
 * `remove` may resolve to `{ warning: string }` — shown as an info toast (e.g. "deleted
 * here, but the mirrored note survived"). `retrySync`, when present, adds a retry button
 * to comments whose SyncStatus is 'Failed' or 'Disabled'.
 */
export interface CommentsAdapter {
    list: () => Promise<CommentItem[]>;
    create: (text: string) => Promise<unknown>;
    update: (id: number, text: string) => Promise<unknown>;
    remove: (id: number) => Promise<unknown>;
    retrySync?: (id: number) => Promise<unknown>;
}

interface Props {
    adapter: CommentsAdapter;
    canEdit: boolean;
    /** Fired after a comment is added/edited/deleted so list badges can refresh. */
    onChanged?: () => void;
    placeholder?: string;
    emptyText?: string;
}

/**
 * Free-text comment thread (add / edit / delete) for any entity.
 *
 * Extracted from ArCommentsThread so /accounts-receivable, the consolidated-business
 * drawer and the Customer-360 Lost-Business tab share one implementation. Behaviour and
 * markup are unchanged from the AR version.
 */
export function CommentsThread({ adapter, canEdit, onChanged, placeholder, emptyText }: Props) {
    const { toast } = useToast();
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [newText, setNewText] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [busy, setBusy] = useState(false);
    // A delete is asked first (design audit 2026-10-07, S1-05): it was one click on an unnamed
    // icon, and a synced comment's deletion also removes the HubSpot note.
    const [pendingDelete, setPendingDelete] = useState<CommentItem | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setComments(await adapter.list());
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load comments', 'error');
        } finally {
            setLoading(false);
        }
    }, [adapter, toast]);

    useEffect(() => { void load(); }, [load]);

    const handleAdd = async () => {
        if (!newText.trim()) return;
        setBusy(true);
        try {
            await adapter.create(newText.trim());
            setNewText('');
            await load();
            onChanged?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to add comment', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleUpdate = async (id: number) => {
        if (!editText.trim()) return;
        setBusy(true);
        try {
            await adapter.update(id, editText.trim());
            setEditingId(null);
            await load();
            onChanged?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to update comment', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id: number) => {
        setBusy(true);
        try {
            const result = await adapter.remove(id);
            if (result && typeof result === 'object' && 'warning' in result
                && typeof (result as { warning: unknown }).warning === 'string') {
                toast((result as { warning: string }).warning, 'info');
            }
            await load();
            onChanged?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to delete comment', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleRetrySync = async (id: number) => {
        if (!adapter.retrySync) return;
        setBusy(true);
        try {
            await adapter.retrySync(id);
            await load();
            onChanged?.();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Sync retry failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-6 text-gray-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
    }

    return (
        <div>
            <div className="mb-3 space-y-2">
                {comments.map(c => (
                    <div key={c.Id} className="rounded border border-gray-200 p-2 text-sm">
                        <div className="mb-1 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{c.CreatedBy}</span>
                                <span className="text-xs text-gray-500">{formatDateTime(c.CreatedAt)}</span>
                                {c.UpdatedAt && <span className="text-xs italic text-gray-500">(edited)</span>}
                                {c.SyncStatus === 'Synced' && c.SyncUrl && (
                                    <a
                                        href={c.SyncUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="View note in HubSpot"
                                        aria-label="View note in HubSpot"
                                        onClick={e => e.stopPropagation()}
                                        className="p-0.5 text-gray-500 hover:text-blue-700"
                                    >
                                        <ExternalLink aria-hidden="true" className="h-3 w-3" />
                                    </a>
                                )}
                                {c.SyncStatus === 'NoDeal' && (
                                    <span
                                        title={c.SyncHint ?? 'This group has no HubSpot deal, so the comment exists only here.'}
                                        className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500"
                                    >
                                        Not in HubSpot (no deal)
                                    </span>
                                )}
                                {(c.SyncStatus === 'Failed' || c.SyncStatus === 'Disabled') && (
                                    <span className="flex items-center gap-1">
                                        <span
                                            title={c.SyncHint ?? undefined}
                                            className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700"
                                        >
                                            HubSpot sync failed
                                        </span>
                                        {canEdit && adapter.retrySync && (
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); handleRetrySync(c.Id); }}
                                                disabled={busy}
                                                aria-label="Retry HubSpot sync"
                                                title="Retry HubSpot sync"
                                                className="p-0.5 text-amber-600 hover:text-amber-800 disabled:opacity-50"
                                            >
                                                <RefreshCw aria-hidden="true" className="h-3 w-3" />
                                            </button>
                                        )}
                                    </span>
                                )}
                            </div>
                            {canEdit && editingId !== c.Id && (
                                <div className="flex items-center gap-1">
                                    <button type="button" aria-label="Edit comment" title="Edit comment" onClick={e => { e.stopPropagation(); setEditingId(c.Id); setEditText(c.CommentText); }} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"><Pencil aria-hidden="true" className="h-3.5 w-3.5" /></button>
                                    <button type="button" aria-label="Delete comment" title="Delete comment" onClick={e => { e.stopPropagation(); setPendingDelete(c); }} disabled={busy} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"><Trash2 aria-hidden="true" className="h-3.5 w-3.5" /></button>
                                </div>
                            )}
                        </div>
                        {editingId === c.Id ? (
                            <div className="mt-1 flex gap-2">
                                <input
                                    type="text"
                                    aria-label="Edit comment"
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(c.Id); if (e.key === 'Escape') { e.stopPropagation(); setEditingId(null); } }}
                                    autoFocus
                                />
                                <button type="button" aria-label="Save comment" title="Save" onClick={e => { e.stopPropagation(); handleUpdate(c.Id); }} disabled={busy} className="rounded p-1.5 text-green-700 hover:bg-gray-100"><Check aria-hidden="true" className="h-4 w-4" /></button>
                                <button type="button" aria-label="Cancel editing" title="Cancel" onClick={e => { e.stopPropagation(); setEditingId(null); }} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"><X aria-hidden="true" className="h-4 w-4" /></button>
                            </div>
                        ) : (
                            <p className="whitespace-pre-wrap text-gray-700">{c.CommentText}</p>
                        )}
                    </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-gray-500">{emptyText ?? 'No comments yet.'}</p>}
            </div>

            {pendingDelete && (
                <ConfirmModal
                    title="Delete this comment?"
                    message={pendingDelete.SyncStatus === 'Synced'
                        ? 'It is removed here and from the HubSpot deal note it was mirrored to.'
                        : 'It is removed for everybody who sees this thread.'}
                    confirmLabel="Delete comment"
                    onConfirm={() => { const id = pendingDelete.Id; setPendingDelete(null); void handleDelete(id); }}
                    onCancel={() => setPendingDelete(null)}
                />
            )}

            {canEdit && (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        placeholder={placeholder ?? 'Add comment…'}
                        aria-label="New comment"
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                    />
                    <button onClick={e => { e.stopPropagation(); handleAdd(); }} disabled={busy || !newText.trim()} className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">Add</button>
                </div>
            )}
        </div>
    );
}
